// R.M.Williams PIM — Phase 1 UI shell
//
// Same shape as Camilla's App.jsx but trimmed to the views Phase 1 covers:
//   - Login (password gate)
//   - Dashboard (KPIs + scrape button + top gap categories)
//   - Products (filterable list + paginated load)
//   - Product detail (image, variant matrix, gaps, AI rewrites)
//   - Settings (clear store, brand peek)
//
// Batch engine, SEO Brief, GSC, Ads, Image Audit, PageSpeed, Compare etc.
// land in later phases. Their hooks and route stubs are commented but disabled.

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import {
  LayoutDashboard, Package, Settings2, Search, Sparkles, RefreshCw, Tag,
  AlertCircle, ChevronRight, ExternalLink, CheckCircle2, XCircle,
  Image as ImageIcon, Lock, Activity, ArrowLeftRight, FileText, AlertTriangle,
} from 'lucide-react';

import {
  C, F, M, DEPTS, STORES, RMW, classifyProduct, computeGaps, computeGapCount,
} from './constants.js';
import { rewriteDescription, rewriteMeta, rewriteTitle, enrichProduct } from './ai.js';

// ─────────────────────────────────────────────────────────────────
// Auth + storage helpers
// ─────────────────────────────────────────────────────────────────

async function checkAuth() {
  try {
    const res = await fetch('/api/auth', { credentials: 'include' });
    return await res.json(); // { authenticated, passwordRequired }
  } catch { return { authenticated: false, passwordRequired: true }; }
}

async function login(password) {
  const res = await fetch('/api/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new Error((await res.json()).error || 'Login failed');
  return res.json();
}

async function logout() {
  await fetch('/api/auth', { method: 'DELETE', credentials: 'include' });
}

async function loadProductsFromDB(storeId) {
  const all = [];
  let offset = 0;
  while (true) {
    const res = await fetch(`/api/products?store=${storeId}&offset=${offset}&limit=2000`, { credentials: 'include' });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    all.push(...(data.products || []));
    if (!data.hasMore) return { products: all, meta: data.meta };
    offset += 2000;
  }
}

async function loadContentPages(storeId) {
  const all = [];
  let offset = 0;
  let typeCounts = [];
  while (true) {
    const res = await fetch(`/api/content-pages?store=${storeId}&offset=${offset}&limit=2000`, { credentials: 'include' });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    all.push(...(data.pages || []));
    typeCounts = data.typeCounts || typeCounts;
    if (!data.hasMore) return { pages: all, typeCounts };
    offset += 2000;
  }
}

async function loadUrlAudit(storeId) {
  const all = [];
  let offset = 0;
  let statusCounts = [];
  while (true) {
    const res = await fetch(`/api/url-audit?store=${storeId}&offset=${offset}&limit=2000`, { credentials: 'include' });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    all.push(...(data.urls || []));
    statusCounts = data.statusCounts || statusCounts;
    if (!data.hasMore) return { urls: all, statusCounts };
    offset += 2000;
  }
}

async function saveEnrichment(storeId, sku, type, data) {
  try {
    await fetch('/api/enrichments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sku, type, data, store: storeId }),
    });
  } catch (e) { console.warn('Enrichment save failed:', e.message); }
}

async function loadEnrichment(storeId, sku, type) {
  try {
    const res = await fetch(`/api/enrichments?store=${storeId}&sku=${sku}&type=${type}`, { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json();
    return json.data;
  } catch { return null; }
}

async function runScrape(storeId, onProgress) {
  // Phase 1: discover URLs from sitemap, then loop scrape calls.
  onProgress?.({ phase: 'discover', message: 'Discovering product URLs from sitemap…' });
  const discRes = await fetch('/api/scrape', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ phase: 'discover', store: storeId }),
  });
  if (!discRes.ok) throw new Error((await discRes.json()).error || `HTTP ${discRes.status}`);
  const disc = await discRes.json();
  if (!disc.productUrls?.length) throw new Error('No product URLs found in sitemap.');

  onProgress?.({ phase: 'scrape', message: `Found ${disc.productUrls.length} products. Scraping…`, total: disc.productUrls.length, processed: 0 });

  let startIdx = 0;
  let totalOk = 0, totalFailed = 0;
  let isFirst = true;
  while (true) {
    const res = await fetch('/api/scrape', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phase: 'scrape', store: storeId, urls: disc.productUrls, startIdx, isFirst }),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    const data = await res.json();
    isFirst = false;
    totalOk += data.ok || 0;
    totalFailed += data.failed || 0;
    onProgress?.({
      phase: 'scrape',
      message: `Scraping ${data.processed}/${data.total} (ok ${totalOk}, failed ${totalFailed})…`,
      total: data.total,
      processed: data.processed,
      ok: totalOk, failed: totalFailed,
    });
    if (data.done) {
      onProgress?.({ phase: 'complete', message: `Done — ${totalOk} ok, ${totalFailed} failed.`, total: data.total, processed: data.processed, ok: totalOk, failed: totalFailed });
      return { ok: totalOk, failed: totalFailed, total: data.total };
    }
    startIdx = data.processed;
  }
}

// ─────────────────────────────────────────────────────────────────
// Reusable bits
// ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = C.a, icon: Icon }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ color: C.tm, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
        {Icon && <Icon size={14} color={color} />}
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, color, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.tm, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Spinner({ size = 14, color = C.a }) {
  return (
    <span style={{
      display: 'inline-block',
      width: size, height: size,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  );
}

function Btn({ children, onClick, disabled, primary, danger, size = 'md', style = {}, title }) {
  const colors = primary
    ? { bg: C.a, fg: C.bg, border: C.a }
    : danger
    ? { bg: 'transparent', fg: C.e, border: C.e + '88' }
    : { bg: C.card, fg: C.t, border: C.b };
  const padding = size === 'sm' ? '6px 10px' : '10px 16px';
  const fontSize = size === 'sm' ? 12 : 13;
  return (
    <button
      onClick={onClick} disabled={disabled} title={title}
      style={{
        background: colors.bg, color: colors.fg, border: `1px solid ${colors.border}`,
        borderRadius: 6, padding, fontSize, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 0.15s ease', ...style,
      }}
    >
      {children}
    </button>
  );
}

const sevColor = { high: C.e, medium: '#f59e0b', low: C.tm };

// ─────────────────────────────────────────────────────────────────
// Login screen
// ─────────────────────────────────────────────────────────────────

function LoginScreen({ onAuthenticated }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, fontFamily: F }}>
      <form
        onSubmit={async e => {
          e.preventDefault();
          setBusy(true); setErr('');
          try { await login(pw); onAuthenticated(); }
          catch (e) { setErr(e.message); }
          finally { setBusy(false); }
        }}
        style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 32, width: 360 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: C.tm, letterSpacing: 1, textTransform: 'uppercase' }}>R.M.Williams</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: C.a, marginTop: 4 }}>PIM</div>
        </div>
        <input
          type="password" value={pw} onChange={e => setPw(e.target.value)}
          placeholder="Password" autoFocus
          style={{ width: '100%', padding: '10px 12px', borderRadius: 6, background: C.input, border: `1px solid ${C.b}`, color: C.t, fontSize: 14 }}
        />
        {err && <div style={{ color: C.e, fontSize: 12, marginTop: 8 }}>{err}</div>}
        <Btn primary onClick={undefined} disabled={busy} style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}>
          {busy ? <Spinner /> : 'Sign in'}
        </Btn>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sidebar
// ─────────────────────────────────────────────────────────────────

function Sidebar({ view, setView, store, onLogout }) {
  const items = [
    { id: 'dashboard', label: 'Dashboard',  icon: LayoutDashboard },
    { id: 'products',  label: 'Products',   icon: Package },
    { id: 'content',   label: 'Content',    icon: FileText },
    { id: 'issues',    label: 'Issues',     icon: AlertTriangle },
    { id: 'settings',  label: 'Settings',   icon: Settings2 },
  ];
  return (
    <aside style={{ width: 220, background: C.card, borderRight: `1px solid ${C.b}`, padding: 16, display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, color: C.tm, letterSpacing: 1.2, textTransform: 'uppercase' }}>R.M.Williams</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: C.a, marginTop: 2 }}>PIM</div>
        <div style={{ fontSize: 10, color: C.td, marginTop: 4, fontFamily: M }}>{store?.flag} {store?.label}</div>
      </div>
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map(it => {
          const Icon = it.icon;
          const active = view === it.id;
          return (
            <button
              key={it.id} onClick={() => setView(it.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6,
                background: active ? C.ad : 'transparent', border: 'none',
                color: active ? C.a : C.tm, fontSize: 13, fontWeight: active ? 600 : 500,
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <Icon size={15} />
              {it.label}
            </button>
          );
        })}
      </nav>
      <button onClick={onLogout} style={{
        background: 'transparent', border: 'none', color: C.tm, fontSize: 11,
        textAlign: 'left', padding: '8px 12px', cursor: 'pointer',
      }}>Sign out</button>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────

function Dashboard({ store, products, meta, onScrape, scrapeStatus, onReload, loading }) {
  const stats = useMemo(() => {
    const total = products.length;
    const totalGaps = products.reduce((s, p) => s + computeGapCount(p), 0);
    const inStock = products.reduce((s, p) => s + ((p.inStockCount || 0) > 0 ? 1 : 0), 0);
    const oos = total - inStock;
    const byDept = {};
    for (const p of products) {
      const d = p._ai_dept || 'other';
      byDept[d] = (byDept[d] || 0) + 1;
    }
    return { total, totalGaps, inStock, oos, byDept };
  }, [products]);

  const topDepts = useMemo(() => Object.entries(stats.byDept).sort((a, b) => b[1] - a[1]).slice(0, 8), [stats]);

  return (
    <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Dashboard</h1>
          <div style={{ color: C.tm, fontSize: 13, marginTop: 4 }}>
            {meta ? `Last scraped ${new Date(meta.scrapedAt).toLocaleString()} · ${meta.totalProducts} products` : 'No catalogue scraped yet.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={onReload} disabled={loading} title="Reload catalogue from DB">
            <RefreshCw size={13} /> Reload
          </Btn>
          <Btn primary onClick={onScrape} disabled={!!scrapeStatus}>
            {scrapeStatus ? <><Spinner color={C.bg} /> Scraping…</> : <><Sparkles size={13} /> Scrape catalogue</>}
          </Btn>
        </div>
      </div>

      {scrapeStatus && (
        <div style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: C.t, fontSize: 13 }}>{scrapeStatus.message}</div>
            {scrapeStatus.total > 0 && (
              <div style={{ color: C.tm, fontFamily: M, fontSize: 11 }}>
                {scrapeStatus.processed}/{scrapeStatus.total}
              </div>
            )}
          </div>
          {scrapeStatus.total > 0 && (
            <div style={{ height: 6, background: C.bl, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(100, (scrapeStatus.processed / scrapeStatus.total) * 100)}%`,
                background: C.a, transition: 'width 0.3s ease',
              }} />
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Products" value={stats.total} sub={meta?.totalProducts ? `of ${meta.totalProducts} expected` : null} icon={Package} color={C.a} />
        <StatCard label="In stock" value={stats.inStock} sub={stats.total ? `${Math.round((stats.inStock / stats.total) * 100)}% coverage` : null} color={C.good} icon={CheckCircle2} />
        <StatCard label="Out of stock" value={stats.oos} sub={stats.oos ? 'review for redirect' : null} color={C.e} icon={XCircle} />
        <StatCard label="Total gaps" value={stats.totalGaps} sub="across all products" color="#f59e0b" icon={AlertCircle} />
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 20 }}>
        <div style={{ fontSize: 12, color: C.tm, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>Top categories</div>
        {topDepts.length === 0 ? (
          <div style={{ color: C.tm, fontSize: 13 }}>No products yet — run a scrape to populate.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12 }}>
            {topDepts.map(([dept, count]) => {
              const d = DEPTS.find(x => x.id === dept) || DEPTS[DEPTS.length - 1];
              return (
                <div key={dept} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: C.hover, borderRadius: 8 }}>
                  <div style={{ fontSize: 22 }}>{d.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{d.label}</div>
                    <div style={{ fontSize: 11, color: C.tm }}>{count} products</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Products list
// ─────────────────────────────────────────────────────────────────

function ProductsList({ store, products, onSelect }) {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('all');
  const [sort, setSort] = useState('gaps');     // 'gaps' | 'title' | 'price-asc' | 'price-desc' | 'oos'
  const [showOnlyOos, setShowOnlyOos] = useState(false);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let out = products.filter(p => {
      if (dept !== 'all' && p._ai_dept !== dept) return false;
      if (showOnlyOos && (p.inStockCount || 0) > 0) return false;
      if (!ql) return true;
      return (p.title || '').toLowerCase().includes(ql)
          || (p.master_sku || '').toLowerCase().includes(ql)
          || (p.tags || []).some(t => String(t).toLowerCase().includes(ql));
    });
    out.sort((a, b) => {
      if (sort === 'title') return (a.title || '').localeCompare(b.title || '');
      if (sort === 'price-asc')  return (a.priceNum || 0) - (b.priceNum || 0);
      if (sort === 'price-desc') return (b.priceNum || 0) - (a.priceNum || 0);
      if (sort === 'oos')   return ((a.inStockCount || 0) - (b.inStockCount || 0));
      return computeGapCount(b) - computeGapCount(a); // 'gaps' default
    });
    return out;
  }, [products, q, dept, sort, showOnlyOos]);

  return (
    <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Products</h1>
      <div style={{ color: C.tm, fontSize: 13, marginTop: 4, marginBottom: 20 }}>
        {filtered.length} of {products.length} showing
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.tm }} />
          <input
            placeholder="Search title, SKU, tags…"
            value={q} onChange={e => setQ(e.target.value)}
            style={{ width: '100%', padding: '9px 12px 9px 32px', background: C.input, border: `1px solid ${C.b}`, borderRadius: 6, color: C.t, fontSize: 13 }}
          />
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ padding: '9px 12px', background: C.input, border: `1px solid ${C.b}`, borderRadius: 6, color: C.t, fontSize: 13 }}>
          <option value="gaps">Most gaps</option>
          <option value="title">Title A–Z</option>
          <option value="price-asc">Price low → high</option>
          <option value="price-desc">Price high → low</option>
          <option value="oos">Stock low → high</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.tm }}>
          <input type="checkbox" checked={showOnlyOos} onChange={e => setShowOnlyOos(e.target.checked)} /> Only OOS
        </label>
      </div>

      {/* Department chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {DEPTS.map(d => {
          const count = d.id === 'all' ? products.length : products.filter(p => p._ai_dept === d.id).length;
          if (d.id !== 'all' && count === 0) return null;
          const active = dept === d.id;
          return (
            <button key={d.id} onClick={() => setDept(d.id)}
              style={{
                padding: '5px 10px', borderRadius: 14, fontSize: 12,
                background: active ? d.color + '22' : C.hover,
                color: active ? d.color : C.tm,
                border: `1px solid ${active ? d.color + '88' : C.b}`,
                cursor: 'pointer', fontWeight: active ? 600 : 500,
              }}>
              <span style={{ marginRight: 4 }}>{d.icon}</span>{d.label} <span style={{ opacity: 0.6 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div style={{ background: C.card, border: `1px dashed ${C.b}`, borderRadius: 10, padding: 40, textAlign: 'center', color: C.tm }}>
          {products.length === 0 ? 'No catalogue scraped yet — head to the Dashboard.' : 'No products match the current filters.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
          {filtered.map(p => <ProductCard key={`${p.master_sku}-${p.storeId}`} product={p} onClick={() => onSelect(p)} />)}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onClick }) {
  const gaps = computeGaps(product);
  const inStock = product.inStockCount || 0;
  const total = product.variantCount || (product.variants?.length || 0);
  return (
    <button onClick={onClick} style={{
      background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 0,
      cursor: 'pointer', textAlign: 'left', overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ aspectRatio: '4 / 3', background: C.bl, position: 'relative', overflow: 'hidden' }}>
        {product.img ? (
          <img src={product.img} alt={product.title || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: C.bl }}
            onError={e => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.tm }}>
            <ImageIcon size={28} />
          </div>
        )}
        {gaps.length > 0 && (
          <div style={{
            position: 'absolute', top: 8, right: 8,
            background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b66',
            padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
          }}>{gaps.length} gap{gaps.length === 1 ? '' : 's'}</div>
        )}
      </div>
      <div style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: C.t }}>{product.title || product.master_sku}</div>
        <div style={{ fontFamily: M, fontSize: 10, color: C.tm }}>{product.master_sku}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.t }}>
            {product.currency} {product.priceRange || product.price}
          </div>
          {total > 0 && (
            <div style={{ fontSize: 11, color: inStock === 0 ? C.e : inStock / total < 0.3 ? '#f59e0b' : C.good }}>
              {inStock}/{total}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Product detail
// ─────────────────────────────────────────────────────────────────

function ProductDetail({ product, store, onBack, onUpdated }) {
  const gaps = computeGaps(product);
  const [working, setWorking] = useState(null); // 'description' | 'title' | 'meta' | 'tags'
  const [results, setResults] = useState({});

  // Load any persisted enrichments on mount
  useEffect(() => {
    (async () => {
      const types = ['description', 'title', 'meta', 'tags'];
      const out = {};
      for (const t of types) {
        const data = await loadEnrichment(store.id, product.master_sku, t);
        if (data) out[t] = data;
      }
      setResults(out);
    })();
  }, [product.master_sku, store.id]);

  const run = async (type, fn) => {
    setWorking(type);
    try {
      const data = await fn(product);
      setResults(r => ({ ...r, [type]: data }));
      await saveEnrichment(store.id, product.master_sku, type, data);
      toast.success(`${type} rewrite complete`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setWorking(null);
    }
  };

  return (
    <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
      <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: C.tm, fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to products
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left column — image + facts + variants */}
        <div>
          <div style={{ aspectRatio: '4 / 3', background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
            {product.img && <img src={product.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.currentTarget.style.display = 'none'} />}
          </div>
          {(product.images || []).length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {product.images.slice(0, 8).map((img, i) => (
                <div key={i} style={{ width: 64, height: 64, borderRadius: 6, background: C.card, border: `1px solid ${C.b}`, overflow: 'hidden' }}>
                  <img src={img.src} alt={img.alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.currentTarget.style.display = 'none'} />
                </div>
              ))}
            </div>
          )}

          <div style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: C.tm, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>Facts</div>
            <FactRow label="Master SKU" value={product.master_sku} mono />
            <FactRow label="Department" value={product._ai_dept || classifyProduct(product)} />
            <FactRow label="Category" value={product.productType || (product.breadcrumbs || []).slice(-2)[0] || '—'} />
            <FactRow label="Price" value={`${product.currency || 'AUD'} ${product.priceRange || product.price}`} />
            <FactRow label="Currency" value={product.currency} />
            <FactRow label="Availability" value={product.availability || '—'} />
            <FactRow label="Stock" value={`${product.inStockCount ?? 0} / ${product.variantCount ?? 0} variants`} />
            <FactRow label="URL" value={product.url ? <a href={product.url} target="_blank" rel="noreferrer" style={{ color: C.a, textDecoration: 'none' }}>{product.url} <ExternalLink size={11} style={{ display: 'inline' }} /></a> : '—'} />
          </div>

          <VariantMatrix product={product} />
        </div>

        {/* Right column — title, gaps, AI rewrite */}
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, marginTop: 0, marginBottom: 4 }}>{product.title || product.master_sku}</h1>
          <div style={{ fontFamily: M, fontSize: 11, color: C.tm, marginBottom: 16 }}>{product.master_sku} · {store.label}</div>

          {/* Description */}
          <Section title="Description">
            <div style={{ fontSize: 13, color: C.t, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {(product.description || product.bodyHtml || '').replace(/<[^>]*>/g, '') || <span style={{ color: C.tm, fontStyle: 'italic' }}>No description scraped.</span>}
            </div>
          </Section>

          {/* Gaps */}
          <Section title={`Gaps (${gaps.length})`} accent="#f59e0b">
            {gaps.length === 0 ? (
              <div style={{ color: C.good, fontSize: 13 }}>✓ No gaps detected.</div>
            ) : gaps.map((g, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < gaps.length - 1 ? `1px solid ${C.bl}` : 'none' }}>
                <div style={{ fontSize: 16 }}>{g.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: C.t }}>{g.label}</div>
                  <div style={{ fontSize: 11, color: C.tm, marginTop: 2 }}>{g.detail}</div>
                </div>
                <div style={{ fontSize: 10, color: sevColor[g.severity], textTransform: 'uppercase', fontWeight: 600 }}>{g.severity}</div>
              </div>
            ))}
          </Section>

          {/* AI rewrites */}
          <Section title="AI rewrite" accent={C.a}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Btn size="sm" onClick={() => run('description', rewriteDescription)} disabled={!!working}>
                {working === 'description' ? <Spinner size={11} /> : <Sparkles size={11} />} Description
              </Btn>
              <Btn size="sm" onClick={() => run('title', rewriteTitle)} disabled={!!working}>
                {working === 'title' ? <Spinner size={11} /> : <Sparkles size={11} />} SEO title
              </Btn>
              <Btn size="sm" onClick={() => run('meta', rewriteMeta)} disabled={!!working}>
                {working === 'meta' ? <Spinner size={11} /> : <Sparkles size={11} />} Meta description
              </Btn>
              <Btn size="sm" onClick={() => run('tags', enrichProduct)} disabled={!!working}>
                {working === 'tags' ? <Spinner size={11} /> : <Tag size={11} />} Tags + enrichment
              </Btn>
            </div>
            {Object.entries(results).map(([type, data]) => (
              <RewriteResult key={type} type={type} data={data} />
            ))}
            {!Object.keys(results).length && (
              <div style={{ color: C.tm, fontSize: 12, fontStyle: 'italic' }}>No rewrites yet — click a button above.</div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function FactRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${C.bl}`, gap: 8 }}>
      <div style={{ fontSize: 11, color: C.tm }}>{label}</div>
      <div style={{ fontSize: 12, color: C.t, fontFamily: mono ? M : 'inherit', textAlign: 'right', wordBreak: 'break-word', maxWidth: '70%' }}>{value || '—'}</div>
    </div>
  );
}

function Section({ title, accent = C.tm, children }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: accent, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10, fontWeight: 600 }}>{title}</div>
      {children}
    </div>
  );
}

function VariantMatrix({ product }) {
  const variants = product.variants || [];
  if (!variants.length) return null;

  const sizes  = [...new Set(variants.map(v => v.size).filter(Boolean))].sort();
  const widths = [...new Set(variants.map(v => v.width).filter(Boolean))].sort();

  if (sizes.length && widths.length) {
    const lookup = {};
    for (const v of variants) lookup[`${v.size}|${v.width}`] = v;
    return (
      <Section title="Variants — Size × Width">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, color: C.tm, fontWeight: 500, fontSize: 11, textAlign: 'center', border: `1px solid ${C.bl}` }}>Size ↓ / Width →</th>
              {widths.map(w => <th key={w} style={{ padding: 6, color: C.tm, fontWeight: 500, fontSize: 11, textAlign: 'center', border: `1px solid ${C.bl}` }}>{w}</th>)}
            </tr>
          </thead>
          <tbody>
            {sizes.map(s => (
              <tr key={s}>
                <th style={{ padding: 6, color: C.tm, fontWeight: 500, fontSize: 11, textAlign: 'center', border: `1px solid ${C.bl}` }}>{s}</th>
                {widths.map(w => {
                  const v = lookup[`${s}|${w}`];
                  if (!v) return <td key={w} style={{ padding: 6, textAlign: 'center', color: C.td, border: `1px solid ${C.bl}` }}>—</td>;
                  return (
                    <td key={w} title={v.availability_message || ''} style={{
                      padding: 6, textAlign: 'center', fontWeight: 600,
                      color: v.available ? C.good : C.e,
                      background: v.available ? C.goodd : '#ef444411',
                      border: `1px solid ${C.bl}`,
                    }}>{v.available ? '✓' : '✗'}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    );
  }

  if (sizes.length) {
    return (
      <Section title="Variants — Size">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {sizes.map(s => {
            const v = variants.find(x => x.size === s);
            return (
              <div key={s} title={v?.availability_message || ''} style={{
                padding: '6px 10px', fontSize: 12, fontFamily: M,
                background: v?.available ? C.goodd : '#ef444411',
                color: v?.available ? C.good : C.e,
                borderRadius: 4, border: `1px solid ${v?.available ? '#22c55e44' : '#ef444444'}`,
              }}>{s}</div>
            );
          })}
        </div>
      </Section>
    );
  }

  return (
    <Section title="Variants">
      <div style={{ fontSize: 12, color: C.tm }}>{variants.length} variant{variants.length === 1 ? '' : 's'} — no size attribute discovered.</div>
    </Section>
  );
}

function RewriteResult({ type, data }) {
  const fields = {
    description: ['description', 'shortDescription', 'keyHighlights'],
    title:       ['title', 'titleLength', 'reasoning'],
    meta:        ['metaDescription', 'metaLength', 'primaryKeywords'],
    tags:        ['tags', 'leatherType', 'constructionNotes', 'fitNotes', 'occasionTags', 'colourFamily', 'careInstructions'],
  }[type] || Object.keys(data || {});

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.bl}` }}>
      <div style={{ fontSize: 11, color: C.a, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{type} result</div>
      {data._raw && <pre style={{ fontSize: 11, color: C.tm, whiteSpace: 'pre-wrap', fontFamily: M }}>{data._raw}</pre>}
      {fields.map(f => {
        if (data[f] == null) return null;
        const v = data[f];
        return (
          <div key={f} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: C.tm, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f}</div>
            <div style={{ fontSize: 13, color: C.t, marginTop: 2 }}>
              {Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Content pages
// ─────────────────────────────────────────────────────────────────

const PAGE_TYPE_META = {
  journal:   { label: 'Journal',     icon: '📰', color: '#60a5fa' },
  help:      { label: 'Help / FAQ',   icon: '❓', color: '#a78bfa' },
  policy:    { label: 'Policy / Legal', icon: '⚖️', color: '#94a3b8' },
  service:   { label: 'Service',      icon: '🧰', color: '#22c55e' },
  brand:     { label: 'Brand Story',  icon: '✦', color: C.a },
  marketing: { label: 'Marketing',    icon: '📣', color: '#f59e0b' },
  category:  { label: 'Category Hub', icon: '🗂', color: '#2dd4bf' },
  unknown:   { label: 'Unknown',      icon: '·',  color: C.tm },
};

function ContentView({ store }) {
  const [pages, setPages] = useState([]);
  const [typeCounts, setTypeCounts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { pages, typeCounts } = await loadContentPages(store.id);
      setPages(pages); setTypeCounts(typeCounts);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  }, [store.id]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return pages.filter(p => {
      if (filter !== 'all' && p.page_type !== filter) return false;
      if (!ql) return true;
      return (p.title || '').toLowerCase().includes(ql)
          || (p.description || '').toLowerCase().includes(ql)
          || (p.url || '').toLowerCase().includes(ql);
    });
  }, [pages, filter, q]);

  if (selected) {
    return <ContentDetail page={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Content pages</h1>
      <div style={{ color: C.tm, fontSize: 13, marginTop: 4, marginBottom: 20 }}>
        {filtered.length} of {pages.length} showing
        {loading && <span style={{ marginLeft: 8 }}><Spinner size={11} /> loading…</span>}
      </div>

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.tm }} />
        <input
          placeholder="Search title, description, URL…"
          value={q} onChange={e => setQ(e.target.value)}
          style={{ width: '100%', padding: '9px 12px 9px 32px', background: C.input, border: `1px solid ${C.b}`, borderRadius: 6, color: C.t, fontSize: 13 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        <button onClick={() => setFilter('all')}
          style={chipStyle(filter === 'all', C.a)}>
          ✨ All ({pages.length})
        </button>
        {typeCounts.map(tc => {
          const meta = PAGE_TYPE_META[tc.page_type] || PAGE_TYPE_META.unknown;
          const active = filter === tc.page_type;
          return (
            <button key={tc.page_type} onClick={() => setFilter(tc.page_type)} style={chipStyle(active, meta.color)}>
              <span style={{ marginRight: 4 }}>{meta.icon}</span>{meta.label} <span style={{ opacity: 0.6 }}>({tc.count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: C.card, border: `1px dashed ${C.b}`, borderRadius: 10, padding: 40, textAlign: 'center', color: C.tm }}>
          {pages.length === 0 ? 'No content pages scraped yet — re-scrape with the new schema deployed.' : 'No content pages match the current filters.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 14 }}>
          {filtered.map(p => <ContentCard key={p.url} page={p} onClick={() => setSelected(p)} />)}
        </div>
      )}
    </div>
  );
}

function chipStyle(active, color) {
  return {
    padding: '5px 10px', borderRadius: 14, fontSize: 12,
    background: active ? color + '22' : C.hover,
    color: active ? color : C.tm,
    border: `1px solid ${active ? color + '88' : C.b}`,
    cursor: 'pointer', fontWeight: active ? 600 : 500,
  };
}

function ContentCard({ page, onClick }) {
  const meta = PAGE_TYPE_META[page.page_type] || PAGE_TYPE_META.unknown;
  return (
    <button onClick={onClick} style={{
      background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, padding: 0,
      cursor: 'pointer', textAlign: 'left', overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      {page.primary_image ? (
        <div style={{ aspectRatio: '16 / 9', background: C.bl, overflow: 'hidden' }}>
          <img src={page.primary_image} alt={page.title || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.currentTarget.style.display = 'none'; }} />
        </div>
      ) : null}
      <div style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: meta.color, fontWeight: 600 }}>{meta.icon} {meta.label}</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: C.t }}>{page.title || page.url}</div>
        {page.description && (
          <div style={{ fontSize: 11, color: C.tm, lineHeight: 1.4 }}>
            {page.description.length > 140 ? page.description.slice(0, 140) + '…' : page.description}
          </div>
        )}
        <div style={{ fontFamily: M, fontSize: 9, color: C.td, marginTop: 'auto' }}>{page.url}</div>
      </div>
    </button>
  );
}

function ContentDetail({ page, onBack }) {
  const meta = PAGE_TYPE_META[page.page_type] || PAGE_TYPE_META.unknown;
  return (
    <div style={{ padding: 32, maxWidth: 960, margin: '0 auto' }}>
      <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: C.tm, fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to content
      </button>

      <div style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 14, background: meta.color + '22', color: meta.color, fontSize: 11, fontWeight: 600, marginBottom: 12 }}>
        {meta.icon} {meta.label}
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 4px' }}>{page.title || page.url}</h1>
      <a href={page.url} target="_blank" rel="noreferrer" style={{ color: C.a, fontSize: 12, fontFamily: M, textDecoration: 'none' }}>
        {page.url} <ExternalLink size={11} style={{ display: 'inline' }} />
      </a>

      {page.primary_image && (
        <div style={{ marginTop: 16, borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.b}` }}>
          <img src={page.primary_image} alt={page.title || ''} style={{ width: '100%', display: 'block' }}
            onError={e => { e.currentTarget.style.display = 'none'; }} />
        </div>
      )}

      {page.description && (
        <Section title="Description">
          <div style={{ fontSize: 14, color: C.t, lineHeight: 1.6 }}>{page.description}</div>
        </Section>
      )}

      {page.body_text && (
        <Section title="Body text (first 3,000 chars)">
          <div style={{ fontSize: 13, color: C.t, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{page.body_text}</div>
        </Section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Issues (URL audit)
// ─────────────────────────────────────────────────────────────────

function IssuesView({ store }) {
  const [urls, setUrls] = useState([]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { urls, statusCounts } = await loadUrlAudit(store.id);
      setUrls(urls); setStatusCounts(statusCounts);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  }, [store.id]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return urls.filter(u => {
      if (filter !== 'all') {
        if (filter === 'http_4xx' && !(u.status_code >= 400 && u.status_code < 500)) return false;
        if (filter === 'http_5xx' && !(u.status_code >= 500 && u.status_code < 600)) return false;
        if (filter === 'http_410' && u.status_code !== 410) return false;
        if (filter === 'http_404' && u.status_code !== 404) return false;
        if (filter === 'no_jsonld' && u.reason !== 'product_no_sku') return false;
        if (filter === 'failed' && u.reason !== 'fetch_failed' && u.reason !== 'timeout') return false;
      }
      if (!ql) return true;
      return (u.url || '').toLowerCase().includes(ql)
          || (u.reason || '').toLowerCase().includes(ql)
          || (u.error || '').toLowerCase().includes(ql);
    });
  }, [urls, filter, q]);

  const total4xx = statusCounts.filter(s => s.status_code >= 400 && s.status_code < 500).reduce((a, b) => a + b.count, 0);
  const total5xx = statusCounts.filter(s => s.status_code >= 500 && s.status_code < 600).reduce((a, b) => a + b.count, 0);
  const total410 = statusCounts.filter(s => s.status_code === 410).reduce((a, b) => a + b.count, 0);
  const total404 = statusCounts.filter(s => s.status_code === 404).reduce((a, b) => a + b.count, 0);

  return (
    <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Issues</h1>
      <div style={{ color: C.tm, fontSize: 13, marginTop: 4, marginBottom: 20 }}>
        {urls.length} URLs in audit · review and redirect dead pages
        {loading && <span style={{ marginLeft: 8 }}><Spinner size={11} /> loading…</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total issues" value={urls.length} color={C.e} icon={AlertTriangle} />
        <StatCard label="HTTP 410 Gone" value={total410} sub="discontinued products" color="#f59e0b" />
        <StatCard label="HTTP 404"      value={total404} sub="missing pages" color={C.e} />
        <StatCard label="HTTP 5xx"      value={total5xx} sub="server errors" color={C.e} />
      </div>

      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.tm }} />
        <input
          placeholder="Search URL, reason, error…"
          value={q} onChange={e => setQ(e.target.value)}
          style={{ width: '100%', padding: '9px 12px 9px 32px', background: C.input, border: `1px solid ${C.b}`, borderRadius: 6, color: C.t, fontSize: 13 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {[
          ['all', `All (${urls.length})`, C.tm],
          ['http_410', `410 Gone (${total410})`, '#f59e0b'],
          ['http_404', `404 Not Found (${total404})`, C.e],
          ['http_4xx', `All 4xx (${total4xx})`, '#f59e0b'],
          ['http_5xx', `5xx Server (${total5xx})`, C.e],
          ['failed', 'Fetch failed', C.tm],
        ].map(([id, label, color]) => (
          <button key={id} onClick={() => setFilter(id)} style={chipStyle(filter === id, color)}>{label}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: C.card, border: `1px dashed ${C.b}`, borderRadius: 10, padding: 40, textAlign: 'center', color: C.tm }}>
          {urls.length === 0 ? 'No issues yet — re-scrape with the new schema deployed.' : 'No URLs match the current filters.'}
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.b}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: C.bl }}>
              <tr>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Reason</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>URL</th>
                <th style={thStyle}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((u, i) => (
                <tr key={u.url} style={{ borderTop: `1px solid ${C.bl}` }}>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600,
                    color: u.status_code >= 500 ? C.e : u.status_code === 410 ? '#f59e0b' : u.status_code >= 400 ? C.e : C.tm }}>
                    {u.status_code ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: C.tm, fontFamily: M, fontSize: 11 }}>{u.reason || '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: M, fontSize: 11, color: C.t, wordBreak: 'break-all' }}>
                    <a href={u.url} target="_blank" rel="noreferrer" style={{ color: C.t, textDecoration: 'none' }}>{u.url}</a>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: C.tm, fontSize: 11, whiteSpace: 'nowrap' }}>
                    {u.last_seen ? new Date(u.last_seen).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 500 && (
            <div style={{ padding: 12, textAlign: 'center', color: C.tm, fontSize: 11, borderTop: `1px solid ${C.bl}` }}>
              showing first 500 of {filtered.length} — refine the filter to narrow down
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 12px', color: C.tm, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' };
const tdStyle = { padding: '8px 12px' };

// ─────────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────────

function SettingsView({ store, onCleared }) {
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ padding: 32, maxWidth: 720, margin: '0 auto' }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Settings</h1>
      <div style={{ color: C.tm, fontSize: 13, marginTop: 4, marginBottom: 24 }}>{store?.flag} {store?.label} · {store?.domain}</div>

      <Section title="Brand voice (read-only — edit src/constants.js)">
        <div style={{ fontSize: 13, color: C.t, lineHeight: 1.6 }}>
          <div><b style={{ color: C.a }}>Voice:</b> {RMW.voice}</div>
          <div style={{ marginTop: 12 }}><b style={{ color: C.a }}>Key messages:</b></div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: C.t }}>
            {RMW.keyMessages.map(m => <li key={m} style={{ marginBottom: 4 }}>{m}</li>)}
          </ul>
        </div>
      </Section>

      <Section title="Danger zone" accent={C.e}>
        <div style={{ fontSize: 13, color: C.tm, marginBottom: 12 }}>
          Wipe all products, enrichments, and scrape metadata for {store?.label}. You'll need to re-scrape afterwards.
        </div>
        <Btn danger disabled={busy} onClick={async () => {
          if (!confirm(`Clear all ${store?.label} data?`)) return;
          setBusy(true);
          try {
            const res = await fetch(`/api/products?store=${store.id}`, { method: 'DELETE', credentials: 'include' });
            if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
            toast.success('Store cleared');
            onCleared();
          } catch (e) { toast.error(e.message); }
          finally { setBusy(false); }
        }}>
          {busy ? <Spinner color={C.e} /> : null} Clear {store?.label} data
        </Btn>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Top-level App
// ─────────────────────────────────────────────────────────────────

export default function App() {
  const [authState, setAuthState] = useState({ checking: true, authenticated: false, passwordRequired: true });
  const [view, setView] = useState('dashboard');
  const [storeId] = useState('au'); // single store in Phase 1
  const store = STORES.find(s => s.id === storeId);

  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [selected, setSelected] = useState(null);

  // ── auth bootstrap ──
  useEffect(() => {
    (async () => {
      const a = await checkAuth();
      setAuthState({ checking: false, authenticated: a.authenticated, passwordRequired: a.passwordRequired });
    })();
  }, []);

  const reload = useCallback(async () => {
    if (!authState.authenticated) return;
    setLoading(true);
    try {
      const { products, meta } = await loadProductsFromDB(storeId);
      setProducts(products);
      setMeta(meta);
    } catch (e) {
      if (e.message === 'Unauthorized') setAuthState(s => ({ ...s, authenticated: false }));
      else toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [authState.authenticated, storeId]);

  useEffect(() => { reload(); }, [reload]);

  const onScrape = async () => {
    setScrapeStatus({ phase: 'discover', message: 'Starting…', total: 0, processed: 0 });
    try {
      const summary = await runScrape(storeId, s => setScrapeStatus(s));
      toast.success(`Scrape complete — ${summary.ok} ok, ${summary.failed} failed`);
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setScrapeStatus(null);
    }
  };

  const onLogout = async () => {
    await logout();
    setAuthState({ checking: false, authenticated: false, passwordRequired: true });
    setProducts([]); setMeta(null); setSelected(null);
  };

  // ── render ──
  if (authState.checking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
        <Spinner size={20} />
      </div>
    );
  }
  if (authState.passwordRequired && !authState.authenticated) {
    return <>
      <Toaster theme="dark" position="top-right" />
      <LoginScreen onAuthenticated={() => setAuthState(s => ({ ...s, authenticated: true }))} />
    </>;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, color: C.t, fontFamily: F }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <Toaster theme="dark" position="top-right" />
      <Sidebar view={view} setView={v => { setView(v); setSelected(null); }} store={store} onLogout={onLogout} />
      <main style={{ flex: 1, overflow: 'auto' }}>
        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div key={`detail-${selected.master_sku}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <ProductDetail product={selected} store={store} onBack={() => setSelected(null)} onUpdated={reload} />
            </motion.div>
          ) : view === 'dashboard' ? (
            <motion.div key="dashboard" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <Dashboard store={store} products={products} meta={meta} onScrape={onScrape} scrapeStatus={scrapeStatus} onReload={reload} loading={loading} />
            </motion.div>
          ) : view === 'products' ? (
            <motion.div key="products" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <ProductsList store={store} products={products} onSelect={setSelected} />
            </motion.div>
          ) : view === 'content' ? (
            <motion.div key="content" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <ContentView store={store} />
            </motion.div>
          ) : view === 'issues' ? (
            <motion.div key="issues" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <IssuesView store={store} />
            </motion.div>
          ) : view === 'settings' ? (
            <motion.div key="settings" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <SettingsView store={store} onCleared={reload} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </main>
    </div>
  );
}
